import {
  useForm,
  validateRequired,
  validateMaxLength,
  FieldType,
  validateEmail
} from 'src/hooks/useForm';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { useActivateBonusViaLink, useGetBonusesTransactions, useLogin } from 'react-easyrocket';
import { Button } from 'src/components/Buttons/Button';
import { PasswordInput } from 'src/components/Inputs/PasswordInput';
import { Input } from 'src/components/Inputs/Input';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { AlternativeAuthMethods } from 'src/components/AlternativeAuthMethods';
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH } from 'src/constants';
import { useModalContext } from 'src/hooks/useModalContext';
import { ACTIVATE_BONUS_LOCAL_STORAGE_KEY } from 'src/constants/activateBonusLocalStorageKey';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

const LoginModal = () => {
  const { mutateAsync: activateBonus } = useActivateBonusViaLink();
  const { refetch } = useGetBonusesTransactions(
    {
      bonus_type: 'All',
      status: ['Pending']
    },
    { isNeedPages: false }
  );
  const { openModal, closeModal, modalProps } = useModalContext<{
    onAuthFlowSuccess?: () => void;
  }>();

  const { mutateAsync: login, isLoading } = useLogin({
    onSuccess: () => {
      closeModal();
      const bonusId = localStorage.getItem(ACTIVATE_BONUS_LOCAL_STORAGE_KEY);
      if (bonusId) {
        activateBonus(
          { bonus_id: bonusId },
          {
            onSuccess: async () => {
              refetch();
            }
          }
        );
        localStorage.removeItem(ACTIVATE_BONUS_LOCAL_STORAGE_KEY);
      }
    }
  });
  const { startRegistration } = useAuthFlow();

  const { t } = useTranslation();
  const {
    errors,
    isFormValid,
    values,
    fieldsProps: { emailProps, passwordProps },
    submitTried,
    setSubmitTried,
    focused
  } = useForm<{
    email: FieldType.Text;
    password: FieldType.Text;
  }>({
    email: {
      placeholder: `${t('general.email')}`,
      validation: [validateRequired, validateMaxLength(MAX_EMAIL_LENGTH), validateEmail],
      fieldType: FieldType.Text
    },
    password: {
      placeholder: `${t('general.password')}`,
      validation: [validateRequired, validateMaxLength(MAX_PASSWORD_LENGTH)],
      fieldType: FieldType.Text
    }
  });

  const handleForgotPasswordClick = () => {
    openModal('RESET_PASSWORD', { replace: true });
  };

  return (
    <ModalWrapper className="max-w-full">
      <div className="flex flex-col items-center gap-[15px] p-[15px] w-full 568:w-[320px]">
        <p className="text-2xl text-lm_title font-bold tracking-[0.03em]">
          {t('modalLogin.title')}
        </p>
        <AlternativeAuthMethods />
        <div className="w-full relative">
          <Input
            {...emailProps}
            id="email"
            isError={submitTried && focused !== 'email' && Boolean(errors.email)}
          />
          <Tooltip
            anchorSelect="#email"
            className="!bg-lm_tooltip_bg text-lm_tooltip_text rounded-4xl z-[1000] !max-w-full"
            place="top"
            isOpen={submitTried && focused !== 'email' && Boolean(errors.email)}>
            <div>{errors.email}</div>
          </Tooltip>
        </div>
        <div className="w-full relative">
          <PasswordInput
            {...passwordProps}
            id="password"
            isError={submitTried && focused !== 'password' && Boolean(errors.password)}
          />
          <Tooltip
            anchorSelect="#password"
            className="!bg-lm_tooltip_bg text-lm_tooltip_text rounded-4xl z-[1000] !max-w-full"
            place="bottom"
            isOpen={submitTried && focused !== 'password' && Boolean(errors.password)}>
            <div>{errors.password}</div>
          </Tooltip>
        </div>
        <p
          className="font-thin text-lm_forgot_password text-sm underline cursor-pointer"
          onClick={handleForgotPasswordClick}>
          {t('modalLogin.forgot')}
        </p>
        <Button
          className="max-w-full"
          loading={isLoading}
          mode="button-primary"
          label={`${t('modalLogin.title')}`}
          onClick={() => {
            setSubmitTried(true);
            if (!isFormValid) return;
            login({ email: values.email, password: values.password });
          }}>
          {t('modalLogin.title')}
        </Button>
        <p className="w-full flex-wrap font-medium text-xs m-auto flex justify-center gap-1 mt-[5px] mb-2.5">
          <span className="text-sm text-lm_text">{t('modalLogin.notRegist')}</span>
          <span
            className="font-thin text-lm_create_account text-sm underline cursor-pointer"
            onClick={() =>
              startRegistration({ replace: true, onSuccess: modalProps.onAuthFlowSuccess })
            }>
            {t('modalLogin.createNewAcc')}
          </span>
        </p>
      </div>
    </ModalWrapper>
  );
};

export default LoginModal;
